import classNames from "classnames";
import { useCallback } from "react";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaArrowRight } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import config from "../../config/config.json";
import { formatTimestamp } from "../../lib/format-timestamp";
import { getStripeConnectAuthUrl } from "../../lib/get-stripe-connect-auth-url";
import { disconnectFromStripe } from "../../services/projectService";
import { Project } from "../../types/Project";
import { StripeConnectInfo } from "../../types/StripeConnectInfo";
import "./StripeConnectCTA.scss";

interface StripeConnectCTAProps {
  project: Project;
  stripeConnectInfo: StripeConnectInfo | null;
}

const StripeConnectCTA: React.FC<StripeConnectCTAProps> = ({
  project,
  stripeConnectInfo,
}) => {
  const navigate = useNavigate();

  const handleConnectClick = useCallback(() => {
    window.localStorage.setItem(
      config.localStorageKeys.stripeConnectProjectId,
      project.id,
    );
    window.location.href = getStripeConnectAuthUrl();
  }, [project]);

  const handleVerifyRevenueClick = useCallback(() => {
    navigate(`/project/${project.id}/public-listing`);
  }, [navigate, project]);

  const handleJortsClick = useCallback(() => {
    navigate(`/projects/${project.id}/jorts`);
  }, [navigate, project]);

  const handleDisconnectClick = useCallback(async () => {
    if (!project?.stripeConnectCode) {
      return;
    }

    const rc = window.confirm(
      `Are you sure you want to disconnect ${project.name} from Stripe?`,
    );
    if (!rc) {
      return;
    }

    await disconnectFromStripe(project.id);
    toast.success("Disconnected from Stripe");
  }, [project]);

  const stripeApiConnected = !!stripeConnectInfo;
  return (
    <div
      className={classNames("stripe-connect-cta", {
        "api-connected": stripeApiConnected,
      })}
    >
      <div className="stripe-connect-cta-header">Why connect to Stripe?</div>
      <div className="stripe-connect-cta-description">
        Stripe is a payment processing platform that allows you to accept
        payments from your customers.{" "}
        <strong>It's popular with startups.</strong>
      </div>
      <div className="reasons">
        <div onClick={handleVerifyRevenueClick}>
          Connect to verfiy your revenue to surface your project to investors.{" "}
          {stripeApiConnected ? (
            <FaArrowRight />
          ) : (
            <Link to="/learn/stripe-connect">Learn more</Link>
          )}
        </div>
        <div onClick={handleJortsClick}>
          Connect to make better sense of your business.{" "}
          {stripeApiConnected ? (
            <FaArrowRight />
          ) : (
            <Link to="/learn/stripe-connect">Learn more</Link>
          )}
        </div>
      </div>
      <Button
        variant="primary"
        onClick={handleConnectClick}
        disabled={!!project.stripeConnectCode || !!stripeConnectInfo}
      >
        Connect to Stripe
      </Button>
      {!!project.stripeConnectCode && !stripeApiConnected && (
        <div className="stripe-connect-status caption-text">
          Your account is being connected.
        </div>
      )}
      {stripeApiConnected && (
        <div className="stripe-connect-status caption-text">
          <div className="status-message">
            Your account is connected as of{" "}
            <strong>{formatTimestamp(project.stripeConnectTimestamp)}</strong>
          </div>
          <span
            className="disconnect-link link-styles"
            onClick={handleDisconnectClick}
          >
            Disconnect
          </span>
        </div>
      )}
    </div>
  );
};

export default StripeConnectCTA;
